<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import store from '@/lib/store';

/**
 * Report entries — training sessions, system updates, and identified risks.
 *
 * These are the three contents of the County's monthly performance report that no system
 * activity can produce; someone has to record them as they happen. One form covers all three
 * because they differ by a single field each, and three near-identical forms would drift.
 *
 * Recording as you go is the point. Reconstructing a quarter of training attendance the week
 * a report is due is how sections end up empty.
 */

const KINDS = [
    { title: 'Training Session', value: 'training' },
    { title: 'System Update', value: 'release' },
    { title: 'Risk', value: 'risk' },
];

const entries = ref([]);
const loading = ref(false);
const kindFilter = ref(null);
const formOpen = ref(false);
const saveError = ref(null);

function emptyDraft() {
    return {
        kind: 'training',
        entry_date: new Date().toISOString().slice(0, 10),
        title: '',
        detail: '',
        attendance: null,
        corrective_action: '',
    };
}

const draft = ref(emptyDraft());

const headers = computed(() => [
    { title: 'Date', key: 'entry_date' },
    { title: 'Kind', key: 'kind' },
    { title: 'Title', key: 'title' },
    { title: 'Details', key: 'detail' },
    { title: 'Attendance', key: 'attendance' },
    { title: 'Corrective Action', key: 'corrective_action' },
    { title: '', key: 'actions', sortable: false },
]);

const rows = computed(() => entries.value.map(entry => ({
    id: entry._id,
    entry_date: new Date(entry.entry_date).toISOString().slice(0, 10),
    kind: KINDS.find(kind => kind.value === entry.kind)?.title || entry.kind,
    title: entry.title,
    detail: entry.detail,
    attendance: entry.attendance ?? '—',
    corrective_action: entry.corrective_action || '—',
})));

async function load() {
    loading.value = true;
    try {
        const response = await store.dispatch('getReportEntryList', kindFilter.value);
        if (response?.success) {
            entries.value = response.data;
        }
    } finally {
        loading.value = false;
    }
}

onBeforeMount(load);

function openForm() {
    draft.value = emptyDraft();
    saveError.value = null;
    formOpen.value = true;
}

async function save() {
    saveError.value = null;
    const response = await store.dispatch('postReportEntry', draft.value);

    // Surfaced rather than swallowed: a validation failure that closes the dialog silently
    // looks exactly like a successful save until the entry is missing from the report.
    if (!response?.success) {
        saveError.value = response?.message || 'Could not save this entry.';
        return;
    }

    formOpen.value = false;
    await load();
}

async function remove(id) {
    const response = await store.dispatch('deleteReportEntry', id);
    if (response?.success) {
        await load();
    }
}
</script>

<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <h1>Report Entries</h1>
                <p class="text-medium-emphasis">
                    Training sessions, system updates, and risks recorded for the monthly performance report.
                </p>
            </v-col>
            <v-col cols="auto">
                <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" @click="openForm">
                    Add Entry
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" sm="3">
                <v-select
                    v-model="kindFilter"
                    :items="KINDS"
                    label="Kind"
                    clearable
                    density="comfortable"
                    @update:modelValue="load"
                />
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-data-table :headers="headers" :items="rows" :loading="loading" :items-per-page="50">
                    <!-- Dynamic slot name, not `#item.actions`: eslint's vue/valid-v-slot
                         reads the dot as a modifier and fails the build. Repo convention. -->
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-btn size="small" variant="text" color="error" @click="remove(item.id)">Delete</v-btn>
                    </template>
                    <template #no-data>
                        <div class="py-4 text-medium-emphasis">Nothing recorded yet.</div>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <v-dialog v-model="formOpen" width="auto">
            <v-card max-width="600" title="Add report entry">
                <v-container>
                    <v-alert v-if="saveError" type="error" density="compact" class="mb-4">
                        {{ saveError }}
                    </v-alert>
                    <v-form>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-select v-model="draft.kind" :items="KINDS" label="Kind" variant="filled" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="draft.entry_date" label="Date" type="date" variant="filled" />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-text-field v-model="draft.title" label="Title" variant="filled" />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-textarea v-model="draft.detail" label="Details" variant="filled" auto-grow />
                            </v-col>
                        </v-row>
                        <!-- Kind-specific fields. The API nulls whichever does not apply, so a
                             stale value left in the form cannot reach the report. -->
                        <v-row v-if="draft.kind === 'training'">
                            <v-col>
                                <v-text-field v-model="draft.attendance" label="Attendance" type="number" min="0" variant="filled" />
                            </v-col>
                        </v-row>
                        <v-row v-if="draft.kind === 'risk'">
                            <v-col>
                                <v-textarea v-model="draft.corrective_action" label="Recommended Corrective Action" variant="filled" auto-grow />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>
                <template #actions>
                    <v-btn @click="save">Save</v-btn>
                    <v-btn text="Cancel" @click="formOpen = false" />
                </template>
            </v-card>
        </v-dialog>
    </v-container>
</template>
